/**
 * AdvancedSearch — Faceted search page for items.
 *
 * Left sidebar: always-visible faceted filters grouped by section (e-commerce style).
 * Right panel: search input, sort controls, result cards with query highlighting.
 *
 * Reuses useItemListData for all filter/sort/search logic.
 */

import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
  Search, X, ChevronDown, ChevronRight, SlidersHorizontal, ArrowUpDown,
  RotateCcw, ExternalLink, Calendar, Check, Loader2, AlertCircle,
  Bookmark, BookmarkCheck, Trash2, Clock,
} from 'lucide-react';
import { useFeatureFlag, FEATURE_FLAGS } from '../../lib/featureFlags';
import {
  useSavedSearches, getSavedSearches,
  type SavedSearch,
} from '../../lib/savedSearches';

import { useItemsStore } from '../../stores/prioritization/itemsStore';
import { useEnterpriseIdentityStore } from '../../stores/prioritization/enterpriseIdentityStore';
import { useTrackStore } from '../../stores/prioritization/trackStore';
import { useSessionStore } from '../../stores/prioritization/sessionStore';
import { useDegapProcessStore } from '../../stores/prioritization/degapProcessStore';import { useItemListData } from '../../components/prioritization/item-list/hooks/useItemListData';
import type { FilterGroup, FilterRule, SortSpec } from '../../components/prioritization/item-list/hooks/useItemListState';
import { CATEGORY_DOT, CATEGORY_BADGE, CATEGORY_BADGE_FALLBACK } from '../../lib/itemCategoryColors';
import { SESSION_STATE_LABELS } from '@/lib/sessions/vocabulary';
import { useKeyboardShortcutHints } from '../../hooks/useKeyboardShortcutHints';

// ============================================================================
// Filter field definitions (same as FilterPanel — kept in sync)
// ============================================================================

interface FilterFieldDef {
  field: string;
  label: string;
  type: 'set' | 'boolean' | 'date-range';
  description?: string;
  options?: { value: string; label: string; dot?: string }[];
  dynamicOptions?: boolean;
}

interface FilterSectionDef {
  label: string;
  fields: FilterFieldDef[];
}

const FILTER_SECTIONS: FilterSectionDef[] = [
  {
    label: 'Item',
    fields: [
      {
        field: 'itemCategory', label: 'Category', type: 'set',
        options: [
          { value: 'problem',     label: 'Problem',     dot: CATEGORY_DOT.problem },
          { value: 'opportunity', label: 'Opportunity', dot: CATEGORY_DOT.opportunity },
          { value: 'risk',        label: 'Risk',        dot: CATEGORY_DOT.risk },
          { value: 'solution',    label: 'Solution',    dot: CATEGORY_DOT.solution },
          { value: 'commitment',  label: 'Commitment',  dot: CATEGORY_DOT.commitment },
          { value: 'investment',  label: 'Investment',  dot: CATEGORY_DOT.investment },
        ],
      },
      {
        field: 'metaCategory', label: 'Subject Type', type: 'set',
        options: [
          { value: 'people',      label: 'People' },
          { value: 'places',      label: 'Places' },
          { value: 'things',      label: 'Things' },
          { value: 'activities',  label: 'Activities' },
          { value: 'information', label: 'Information' },
          { value: 'time',        label: 'Time' },
        ],
      },
      {
        field: 'exit_status', label: 'Status', type: 'set',
        options: [
          { value: 'active',   label: 'Active',   dot: 'bg-[var(--green)]' },
          { value: 'deferred', label: 'Deferred', dot: 'bg-[var(--mustard)]' },
          { value: 'archived', label: 'Archived', dot: 'bg-[var(--surface)]' },
        ],
      },
      {
        field: 'altitude', label: 'Altitude', type: 'set',
        options: [
          { value: '1', label: 'Level 1' },
          { value: '2', label: 'Level 2' },
          { value: '3', label: 'Level 3' },
          { value: '4', label: 'Level 4' },
          { value: '5', label: 'Level 5' },
        ],
      },
      {
        field: 'source', label: 'Source', type: 'set',
        options: [
          { value: 'customer_feedback',    label: 'Customer Feedback' },
          { value: 'team_discussion',      label: 'Team Discussion' },
          { value: 'stakeholder_request',  label: 'Stakeholder Request' },
          { value: 'market_research',      label: 'Market Research' },
          { value: 'competitor_analysis',  label: 'Competitor Analysis' },
          { value: 'internal_ops',         label: 'Internal Operations' },
          { value: 'strategic_initiative', label: 'Strategic Initiative' },
          { value: 'bug_or_issue',         label: 'Bug or Issue' },
          { value: 'other',               label: 'Other' },
        ],
      },
      {
        field: 'dedupeStatus', label: 'Dedupe Status', type: 'set',
        options: [
          { value: 'pending',      label: 'Pending' },
          { value: 'unique',       label: 'Unique' },
          { value: 'merged',       label: 'Merged' },
          { value: 'duplicate_of', label: 'Duplicate' },
        ],
      },
    ],
  },
  {
    label: 'Completeness',
    fields: [
      { field: 'readyForRanking', label: 'Base Complete', type: 'boolean', description: 'Has title, category, altitude, and dedupe decision' },
      { field: 'isStale',         label: 'Stale',         type: 'boolean', description: 'Not updated in the last 30 days' },
    ],
  },
  {
    label: 'Relationships',
    fields: [
      { field: 'hasCluster',     label: 'In a Cluster',    type: 'boolean', description: 'Grouped by identity with other items' },
      { field: 'hasPackage',     label: 'In a Package',    type: 'boolean', description: 'Must be prioritized or selected together' },
      { field: 'hasPredecessors', label: 'Has predecessors', type: 'boolean', description: 'Must be addressed after something else' },
      { field: 'hasDependents',   label: 'Has dependents',   type: 'boolean', description: 'Items waiting on this item' },
      { field: 'hasCompositionParts', label: 'Has composition parts', type: 'boolean', description: 'This item is a whole with parts' },
      { field: 'isPartOfComposition', label: 'Is part of a whole', type: 'boolean', description: 'This item is a part in a composition' },
      { field: 'hasElaborationPossibilities', label: 'Has elaboration possibilities', type: 'boolean', description: 'Parent need with a possibility continuum' },
      { field: 'isElaborationPossibility', label: 'Is an elaboration possibility', type: 'boolean', description: 'Possibility on a parent need continuum' },
      { field: 'hasContainsChildren', label: 'Has contains children', type: 'boolean', description: 'Reporting parent with child items' },
      { field: 'hasContainsParent', label: 'Has contains parent', type: 'boolean', description: 'Reporting child of a parent item' },
      { field: 'hasFrames',      label: 'Has Additional Categories', type: 'boolean', description: 'Item has more than one category' },
    ],
  },
  {
    label: 'Lineage',
    fields: [
      { field: 'isLeadershipApproved',  label: 'On Priority List',         type: 'boolean', description: 'Approved by leadership via Catchball or item pool' },
      { field: 'inItemPool',            label: 'In Item Pool',             type: 'boolean', description: 'Curated into at least one prioritization pool' },
      { field: 'hasCatchballProposal',  label: 'Has Catchball Proposal',   type: 'boolean', description: 'A proposal has been submitted for this item' },
      {
        field: 'catchballStatus', label: 'Catchball Outcome', type: 'set',
        options: [
          { value: 'pending',   label: 'Pending',   dot: 'bg-[var(--mustard)]' },
          { value: 'accepted',  label: 'Accepted',  dot: 'bg-[var(--green)]' },
          { value: 'countered', label: 'Returned',  dot: 'bg-[var(--coral)]' },
          { value: 'rejected',  label: 'Rejected',  dot: 'bg-[var(--coral)]' },
          { value: 'withdrawn', label: 'Withdrawn', dot: 'bg-[var(--surface)]' },
        ],
      },
    ],
  },
  {
    label: 'Activity',
    fields: [
      { field: 'cycleId',   label: 'Cycle',   type: 'set', dynamicOptions: true },
      { field: 'trackId',   label: 'Track',   type: 'set', dynamicOptions: true },
      { field: 'sessionId', label: 'Session', type: 'set', dynamicOptions: true },
    ],
  },
  {
    label: 'Queue',
    fields: [
      { field: 'queue', label: 'Queue', type: 'set', dynamicOptions: true },
    ],
  },
  {
    label: 'Dates',
    fields: [
      { field: 'createdAt', label: 'Created Date',  type: 'date-range', description: 'When the item was created' },
      { field: 'updatedAt', label: 'Updated Date',  type: 'date-range', description: 'When the item was last modified' },
    ],
  },
];

export const ALL_FILTER_FIELDS = FILTER_SECTIONS.flatMap((s) => s.fields);

// ============================================================================
// Entity types
// ============================================================================

export type EntityType = 'items' | 'cycles' | 'tracks' | 'sessions';

// ── Cycle filter sections ─────────────────────────────────────────────────────

const CYCLE_FILTER_SECTIONS: FilterSectionDef[] = [
  {
    label: 'Cycle',
    fields: [
      {
        field: 'status', label: 'Status', type: 'set',
        options: [
          { value: 'draft',     label: 'Draft',     dot: 'bg-[var(--surface)]' },
          { value: 'active',    label: 'Active',    dot: 'bg-[var(--green)]' },
          { value: 'paused',    label: 'Paused',    dot: 'bg-[var(--mustard)]' },
          { value: 'completed', label: 'Completed', dot: 'bg-[var(--slate)]' },
          { value: 'archived',  label: 'Archived',  dot: 'bg-[var(--rule-bright)]' },
        ],
      },
      {
        field: 'currentPhase', label: 'Phase', type: 'set',
        options: [
          { value: 'DECIDE',     label: 'Decide' },
          { value: 'ENGAGE',     label: 'Engage' },
          { value: 'GATHER',     label: 'Gather' },
          { value: 'ARRANGE',    label: 'Arrange' },
          { value: 'PRIORITIZE', label: 'Prioritize' },
        ],
      },
      {
        field: 'cadence', label: 'Cadence', type: 'set',
        options: [
          { value: 'episodic',   label: 'Episodic' },
          { value: 'periodic',   label: 'Periodic' },
          { value: 'continuous', label: 'Continuous' },
        ],
      },
    ],
  },
  {
    label: 'Dates',
    fields: [
      { field: 'createdAt',   label: 'Created Date',   type: 'date-range' },
      { field: 'startedAt',   label: 'Started Date',   type: 'date-range' },
      { field: 'completedAt', label: 'Completed Date', type: 'date-range' },
    ],
  },
];

// ── Track filter sections ─────────────────────────────────────────────────────

const TRACK_FILTER_SECTIONS: FilterSectionDef[] = [
  {
    label: 'Track',
    fields: [
      {
        field: 'status', label: 'Status', type: 'set',
        options: [
          { value: 'active',         label: 'Active',         dot: 'bg-[var(--green)]' },
          { value: 'accepted',       label: 'Accepted',       dot: 'bg-[var(--slate)]' },
          { value: 'final_rejected', label: 'Final Rejected', dot: 'bg-[var(--coral)]' },
          { value: 'archived',       label: 'Archived',       dot: 'bg-[var(--surface)]' },
        ],
      },
      {
        field: 'requiredItemCategory', label: 'Item Category', type: 'set',
        options: [
          { value: 'problem',     label: 'Problem',     dot: CATEGORY_DOT.problem },
          { value: 'opportunity', label: 'Opportunity', dot: CATEGORY_DOT.opportunity },
          { value: 'risk',        label: 'Risk',        dot: CATEGORY_DOT.risk },
          { value: 'solution',    label: 'Solution',    dot: CATEGORY_DOT.solution },
          { value: 'commitment',  label: 'Commitment',  dot: CATEGORY_DOT.commitment },
          { value: 'investment',  label: 'Investment',  dot: CATEGORY_DOT.investment },
        ],
      },
      {
        field: 'requiredAltitudeLevel', label: 'Altitude', type: 'set',
        options: [
          { value: '1', label: 'Level 1' },
          { value: '2', label: 'Level 2' },
          { value: '3', label: 'Level 3' },
          { value: '4', label: 'Level 4' },
          { value: '5', label: 'Level 5' },
        ],
      },
      { field: 'isStandalone', label: 'Standalone', type: 'boolean', description: 'Not tied to a cycle' },
    ],
  },
  {
    label: 'Dates',
    fields: [
      { field: 'createdAt', label: 'Created Date', type: 'date-range' },
    ],
  },
];

// ── Session filter sections ───────────────────────────────────────────────────

const SESSION_FILTER_SECTIONS: FilterSectionDef[] = [
  {
    label: 'Session',
    fields: [
      {
        field: 'sessionType', label: 'Type', type: 'set',
        options: [
          { value: 'engage',  label: 'Engage' },
          { value: 'gather',  label: 'Gather' },
          { value: 'arrange', label: 'Arrange' },
        ],
      },
      {
        field: 'state', label: 'State', type: 'set',
        options: [
          { value: 'DRAFT',              label: 'Draft',      dot: 'bg-[var(--surface)]' },
          { value: 'CRITERIA_FINALIZED', label: 'Finalized',  dot: 'bg-[var(--mustard)]' },
          { value: 'RUN_COMPLETE',       label: 'Completed',  dot: 'bg-[var(--slate)]' },
          { value: 'PUBLISHED',          label: 'Published',  dot: 'bg-[var(--green)]' },
        ],
      },
      { field: 'cycleId', label: 'Cycle', type: 'set', dynamicOptions: true },
      { field: 'trackId', label: 'Track', type: 'set', dynamicOptions: true },
    ],
  },
  {
    label: 'Dates',
    fields: [
      { field: 'createdAt',    label: 'Created Date',   type: 'date-range' },
      { field: 'scheduledFor', label: 'Scheduled Date', type: 'date-range' },
    ],
  },
];

// ── Entity → filter sections map ─────────────────────────────────────────────

const ENTITY_FILTER_SECTIONS: Record<EntityType, FilterSectionDef[]> = {
  items:    FILTER_SECTIONS,
  cycles:   CYCLE_FILTER_SECTIONS,
  tracks:   TRACK_FILTER_SECTIONS,
  sessions: SESSION_FILTER_SECTIONS,
};

// ============================================================================
// Sort options
// ============================================================================

const SORT_OPTIONS: { field: string; label: string; defaultDir: 'asc' | 'desc' }[] = [
  { field: 'updatedAt',    label: 'Recently Updated',  defaultDir: 'desc' },
  { field: 'createdAt',    label: 'Recently Created',  defaultDir: 'desc' },
  { field: 'title',        label: 'Title (A–Z)',        defaultDir: 'asc'  },
  { field: 'exit_status',  label: 'Status',             defaultDir: 'asc'  },
  { field: 'itemCategory', label: 'Category',           defaultDir: 'asc'  },
  { field: 'altitude',     label: 'Altitude',           defaultDir: 'asc'  },
  { field: 'readiness',    label: 'Readiness',          defaultDir: 'asc'  },
];

const CYCLE_SORT_OPTIONS: { field: string; label: string; defaultDir: 'asc' | 'desc' }[] = [
  { field: 'updatedAt',    label: 'Recently Updated', defaultDir: 'desc' },
  { field: 'createdAt',    label: 'Recently Created', defaultDir: 'desc' },
  { field: 'name',         label: 'Name (A–Z)',        defaultDir: 'asc'  },
  { field: 'status',       label: 'Status',            defaultDir: 'asc'  },
  { field: 'currentPhase', label: 'Phase',             defaultDir: 'asc'  },
];

const TRACK_SORT_OPTIONS: { field: string; label: string; defaultDir: 'asc' | 'desc' }[] = [
  { field: 'updatedAt',            label: 'Recently Updated', defaultDir: 'desc' },
  { field: 'createdAt',            label: 'Recently Created', defaultDir: 'desc' },
  { field: 'name',                 label: 'Name (A–Z)',        defaultDir: 'asc'  },
  { field: 'status',               label: 'Status',            defaultDir: 'asc'  },
  { field: 'requiredItemCategory', label: 'Category',          defaultDir: 'asc'  },
  { field: 'requiredAltitudeLevel',label: 'Altitude',          defaultDir: 'asc'  },
];

const SESSION_SORT_OPTIONS: { field: string; label: string; defaultDir: 'asc' | 'desc' }[] = [
  { field: 'updatedAt',    label: 'Recently Updated', defaultDir: 'desc' },
  { field: 'createdAt',    label: 'Recently Created', defaultDir: 'desc' },
  { field: 'name',         label: 'Name (A–Z)',        defaultDir: 'asc'  },
  { field: 'sessionType',  label: 'Type',              defaultDir: 'asc'  },
  { field: 'state',        label: 'State',             defaultDir: 'asc'  },
  { field: 'scheduledFor', label: 'Scheduled Date',    defaultDir: 'asc'  },
];

const ENTITY_SORT_OPTIONS: Record<EntityType, { field: string; label: string; defaultDir: 'asc' | 'desc' }[]> = {
  items:    SORT_OPTIONS,
  cycles:   CYCLE_SORT_OPTIONS,
  tracks:   TRACK_SORT_OPTIONS,
  sessions: SESSION_SORT_OPTIONS,
};

// ============================================================================
// Highlight helper
// ============================================================================

function Highlight({ text, query }: { text: string; query: string }) {
  if (!query.trim() || !text) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-[var(--mustard-dim)] text-[var(--mustard)] not-italic px-0.5">
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  );
}

// ============================================================================
// Active filter chip
// ============================================================================

interface ActiveChip {
  field: string;
  label: string;
  displayValue: string;
  onRemove: () => void;
}

function ActiveFilterChips({ chips, onClearAll }: { chips: ActiveChip[]; onClearAll: () => void }) {
  if (chips.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5 px-4 py-2.5 bg-[var(--slate-dim)]/60 border-b border-[var(--slate)]">
      <span className="text-[11px] font-semibold text-[var(--text-ter)] uppercase tracking-wider mr-0.5">Active:</span>
      {chips.map((chip, i) => (
        <span
          key={i}
          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-[var(--bg-raised)] border border-[var(--slate)] text-[var(--text-ter)] rounded-full shadow-sm"
        >
          <span className="text-[var(--text-ter)]">{chip.label}:</span>
          {chip.displayValue}
          <button
            onClick={chip.onRemove}
            className="ml-0.5 text-[var(--text-ter)] hover:text-[var(--text-ter)] transition-colors"
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      <button
        onClick={onClearAll}
        className="text-[11px] text-[var(--text-ter)] hover:text-[var(--text-ter)] font-medium ml-1 transition-colors"
      >
        Clear all
      </button>
    </div>
  );
}

// ============================================================================
// FacetFieldSet — handles a single "set" field with search + truncation.
//
// Correctly handles zero / one / few / many:
//   0 items   → hidden (caller skips it)
//   1–6 items → all shown, no search bar
//   7+ items  → search bar + truncate to 6 unselected, "N more" expansion
//
// Selected items are always pinned above the unselected list so you never
// lose track of what you've chosen regardless of list length.
// ============================================================================

const FACET_VISIBLE_LIMIT = 6;

interface FacetFieldSetProps {
  field: string;
  options: { value: string; label: string; dot?: string }[];
  selected: Set<string>;
  onToggle: (value: string) => void;
}

function FacetFieldSet({ field: _field, options, selected, onToggle }: FacetFieldSetProps) {
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(false);

  // Split into selected and unselected pools
  const selectedOpts  = options.filter((o) => selected.has(o.value));
  const unselectedOpts = options.filter((o) => !selected.has(o.value));

  // Apply search to the unselected pool only (selected are always shown)
  const filteredUnselected = search
    ? unselectedOpts.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()))
    : unselectedOpts;

  // Truncate unselected when not expanded (and no active search)
  const showSearch = options.length > FACET_VISIBLE_LIMIT;
  const truncated = !expanded && !search
    ? filteredUnselected.slice(0, FACET_VISIBLE_LIMIT)
    : filteredUnselected;
  const hiddenCount = !expanded && !search
    ? Math.max(0, filteredUnselected.length - FACET_VISIBLE_LIMIT)
    : 0;

  const renderOption = (opt: { value: string; label: string; dot?: string }, isChecked: boolean) => (
    <button
      key={opt.value}
      onClick={() => onToggle(opt.value)}
      className={`w-full flex items-center gap-2 px-2 py-1.5  text-left transition-all duration-100 ${
        isChecked ? 'bg-[var(--slate-dim)] text-[var(--text-sec)]' : 'hover:bg-[var(--surface)] text-[var(--text-sec)]'
      }`}
    >
      <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center flex-shrink-0 transition-all ${
        isChecked ? 'bg-[var(--slate)] border-[var(--slate)]' : 'border-[var(--rule)] bg-[var(--bg-raised)]'
      }`}>
        {isChecked && <Check className="h-2 w-2 text-white" strokeWidth={3} />}
      </div>
      {opt.dot && <span className={`w-2 h-2 rounded-full flex-shrink-0 ${opt.dot}`} />}
      <span className="text-xs font-medium leading-tight truncate">{opt.label}</span>
    </button>
  );

  return (
    <div className="space-y-0.5">
      {/* Search input — only appears when list is long */}
      {showSearch && (
        <div className="relative mb-1">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-[var(--text-ter)] pointer-events-none" />
          <input
            type="text"
            placeholder="Find…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setExpanded(false); }}
            className="w-full text-xs pl-6 pr-6 py-1.5 border border-[var(--rule)] bg-[var(--surface)] focus:outline-none focus:border-[var(--slate)] focus:ring-1 focus:ring-indigo-200 placeholder:text-[var(--text-ter)]"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[var(--text-ter)] hover:text-[var(--text-sec)]"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      )}

      {/* Selected items — always shown at top, separated by a subtle divider */}
      {selectedOpts.length > 0 && (
        <>
          {selectedOpts.map((o) => renderOption(o, true))}
          {(truncated.length > 0 || hiddenCount > 0) && (
            <div className="h-px bg-[var(--hover-tint)] my-1" />
          )}
        </>
      )}

      {/* Visible unselected items */}
      {truncated.map((o) => renderOption(o, false))}

      {/* Zero unselected + search active → no match message */}
      {search && filteredUnselected.length === 0 && selectedOpts.length === 0 && (
        <p className="text-[11px] text-[var(--text-ter)] px-2 py-1">No matches for "{search}"</p>
      )}

      {/* Show more / less toggle */}
      {hiddenCount > 0 && (
        <button
          onClick={() => setExpanded(true)}
          className="w-full text-left text-[11px] text-[var(--text-ter)] hover:text-[var(--text-ter)] px-2 py-1 transition-colors"
        >
          + {hiddenCount} more
        </button>
      )}
      {expanded && !search && filteredUnselected.length > FACET_VISIBLE_LIMIT && (
        <button
          onClick={() => setExpanded(false)}
          className="w-full text-left text-[11px] text-[var(--text-ter)] hover:text-[var(--text-sec)] px-2 py-1 transition-colors"
        >
          Show less
        </button>
      )}
    </div>
  );
}

// ============================================================================
// Facet section
// ============================================================================

interface FacetSectionProps {
  section: FilterSectionDef;
  facetValues: Record<string, Set<string>>;
  dateRanges: Record<string, [string, string]>;
  dynamicOptionsMap: Record<string, { value: string; label: string }[]>;
  onToggleFacet: (field: string, value: string) => void;
  onSetDate: (field: string, end: 'start' | 'end', value: string) => void;
}

function FacetSection({
  section, facetValues, dateRanges, dynamicOptionsMap,
  onToggleFacet, onSetDate,
}: FacetSectionProps) {
  const [open, setOpen] = useState(true);

  // Check if any field in section has active values
  const hasActive = section.fields.some((f) => {
    if (f.type === 'date-range') {
      const r = dateRanges[f.field];
      return r && (r[0] || r[1]);
    }
    return (facetValues[f.field]?.size ?? 0) > 0;
  });

  return (
    <div className={`border-b border-[var(--rule)] ${hasActive ? 'bg-[var(--slate-dim)]/30' : ''}`}>
      <button
        onClick={() => setOpen((p) => !p)}
        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-[var(--surface)]/80 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-bold text-[var(--text-ter)] uppercase tracking-widest">{section.label}</span>
          {hasActive && (
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--slate)] flex-shrink-0" />
          )}
        </div>
        {open
          ? <ChevronDown className="h-3.5 w-3.5 text-[var(--text-ter)]" />
          : <ChevronRight className="h-3.5 w-3.5 text-[var(--text-ter)]" />
        }
      </button>

      {open && (
        <div className="pb-3 space-y-3">
          {section.fields.map((fieldDef) => {
            const fieldOpts = fieldDef.dynamicOptions
              ? (dynamicOptionsMap[fieldDef.field] ?? [])
              : (fieldDef.options ?? []);
            const selected = facetValues[fieldDef.field] ?? new Set<string>();
            const dateRange = dateRanges[fieldDef.field] ?? ['', ''];

            // Hide empty dynamic fields
            if (fieldDef.dynamicOptions && fieldOpts.length === 0) return null;

            const showFieldLabel = section.fields.length > 1;

            return (
              <div key={fieldDef.field} className="px-4 space-y-1.5">
                {showFieldLabel && (
                  <div className="text-[11px] font-semibold text-[var(--text-sec)] mt-1">
                    {fieldDef.label}
                    {selected.size > 0 && (
                      <span className="ml-1.5 text-[var(--text-ter)]">({selected.size})</span>
                    )}
                  </div>
                )}

                {fieldDef.type === 'boolean' && (
                  <div className="flex gap-1.5">
                    {[{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }].map((opt) => {
                      const isActive = selected.has(opt.value);
                      return (
                        <button
                          key={opt.value}
                          onClick={() => onToggleFacet(fieldDef.field, opt.value)}
                          className={`px-3 py-1 text-xs font-semibold rounded-full border transition-all duration-150 ${
                            isActive
                              ? 'bg-[var(--slate)] border-[var(--slate)] text-white shadow-sm'
                              : 'bg-[var(--bg-raised)] border-[var(--rule)] text-[var(--text-sec)] hover:border-[var(--slate)] hover:text-[var(--text-ter)]'
                          }`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                )}

                {fieldDef.type === 'set' && fieldOpts.length > 0 && (
                  <FacetFieldSet
                    field={fieldDef.field}
                    options={fieldOpts}
                    selected={selected}
                    onToggle={(v) => onToggleFacet(fieldDef.field, v)}
                  />
                )}

                {fieldDef.type === 'date-range' && (
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-1.5">
                      <Calendar className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
                      <span className="text-[11px] text-[var(--text-ter)]">From</span>
                    </div>
                    <input
                      type="date"
                      value={dateRange[0]}
                      max={dateRange[1] || undefined}
                      onChange={(e) => onSetDate(fieldDef.field, 'start', e.target.value)}
                      className="w-full text-xs px-2.5 py-1.5 border border-[var(--rule)] bg-[var(--surface)] focus:outline-none focus:border-[var(--slate)] focus:ring-1 focus:ring-indigo-200 text-[var(--text-sec)]"
                    />
                    <span className="text-[11px] text-[var(--text-ter)] ml-5 block">To</span>
                    <input
                      type="date"
                      value={dateRange[1]}
                      min={dateRange[0] || undefined}
                      onChange={(e) => onSetDate(fieldDef.field, 'end', e.target.value)}
                      className="w-full text-xs px-2.5 py-1.5 border border-[var(--rule)] bg-[var(--surface)] focus:outline-none focus:border-[var(--slate)] focus:ring-1 focus:ring-indigo-200 text-[var(--text-sec)]"
                    />
                    {(dateRange[0] || dateRange[1]) && (
                      <button
                        onClick={() => { onSetDate(fieldDef.field, 'start', ''); onSetDate(fieldDef.field, 'end', ''); }}
                        className="text-[11px] text-[var(--text-ter)] hover:text-[var(--text-sec)] flex items-center gap-1 transition-colors"
                      >
                        <X className="h-3 w-3" />
                        Clear dates
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Entity type selector tabs
// ============================================================================

const ENTITY_META: Record<EntityType, { label: string; pluralLabel: string; color: string; bg: string; border: string; activeBg: string; activeText: string; activeBorder: string }> = {
  items:    { label: 'Items',    pluralLabel: 'items',    color: 'text-[var(--text-sec)]',  bg: 'bg-[var(--slate-dim)]',  border: 'border-[var(--slate)]',  activeBg: 'bg-[var(--slate)]',  activeText: 'text-white', activeBorder: 'border-[var(--slate)]' },
  cycles:   { label: 'Cycles',   pluralLabel: 'cycles',   color: 'text-[var(--text-sec)]',  bg: 'bg-[var(--slate-dim)]',  border: 'border-[var(--slate)]',  activeBg: 'bg-[var(--slate)]',  activeText: 'text-white', activeBorder: 'border-[var(--slate)]' },
  tracks:   { label: 'Tracks', pluralLabel: 'Tracks', color: 'text-[var(--mustard)]',  bg: 'bg-[var(--mustard-dim)]',  border: 'border-[var(--mustard)]',  activeBg: 'bg-[var(--mustard)]',  activeText: 'text-white', activeBorder: 'border-[var(--mustard)]' },
  sessions: { label: 'Sessions', pluralLabel: 'sessions', color: 'text-[var(--green)] dark:text-[var(--text-ter)]', bg: 'bg-[var(--green-dim)]', border: 'border-[var(--green)]', activeBg: 'bg-[var(--green)]', activeText: 'text-white', activeBorder: 'border-[var(--green)]' },
};

interface EntityTabsProps {
  value: EntityType | null;
  counts: Partial<Record<EntityType, number>>;
  onChange: (v: EntityType) => void;
}

function EntityTabs({ value, counts, onChange }: EntityTabsProps) {
  return (
    <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-[var(--rule)] bg-[var(--bg-raised)] flex-shrink-0">

      {/* Standalone filter trigger — clicking it opens Items filters by default */}
      <button
        onClick={() => onChange('items')}
        title={value !== null ? 'Filters active' : 'Open filters'}
        className={`flex items-center gap-1.5 px-2.5 py-1.5  text-xs font-semibold border transition-all duration-150 mr-1 ${
          value !== null
            ? 'bg-[var(--hover-tint)] border-[var(--rule)] text-[var(--text-sec)]'
            : 'bg-[var(--surface)] border-[var(--rule)] text-[var(--text-ter)] hover:bg-[var(--hover-tint)] hover:border-[var(--rule)] hover:text-[var(--text-sec)]'
        }`}
      >
        <SlidersHorizontal className="h-3.5 w-3.5" />
        <span>Filters</span>
      </button>

      <span className="text-[var(--text-ter)] text-xs select-none">·</span>

      {(Object.keys(ENTITY_META) as EntityType[]).map((et) => {
        const meta = ENTITY_META[et];
        const isActive = value === et;
        const count = counts[et];
        return (
          <button
            key={et}
            onClick={() => onChange(et)}
            title={isActive ? `Click to clear ${meta.label} filter` : `Filter to ${meta.label}`}
            className={`flex items-center gap-1.5 px-3 py-1.5  text-xs font-semibold border transition-all duration-150 ${
              isActive
                ? `${meta.activeBg} ${meta.activeText} ${meta.activeBorder} shadow-sm`
                : `${meta.color} ${meta.bg} ${meta.border} hover:opacity-80`
            }`}
          >
            <SlidersHorizontal className="h-3 w-3 opacity-60 flex-shrink-0" />
            {meta.label}
            {count !== undefined && (
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                isActive ? 'bg-[var(--bg-raised)]/25 text-white' : `${meta.color} ${meta.bg}`
              }`}>
                {count.toLocaleString()}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ============================================================================
// Non-item result cards
// ============================================================================

function CycleCard({ cycle, searchQuery }: { cycle: any; searchQuery: string }) {
  const statusColors: Record<string, string> = {
    draft:     'bg-[var(--hover-tint)] text-[var(--text-ter)] border-[var(--rule)]',
    active:    'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] border-[var(--green)]',
    paused:    'bg-[var(--mustard-dim)] text-[var(--mustard)] border-[var(--mustard)]',
    completed: 'bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--slate)]',
    archived:  'bg-[var(--hover-tint)] text-[var(--text-ter)] border-[var(--rule)]',
  };
  const phaseDots: Record<string, string> = {
    DECIDE:     'bg-[var(--surface)]',
    ENGAGE:     'bg-[var(--slate)]',
    GATHER:     'bg-[var(--mustard)]',
    ARRANGE:    'bg-[var(--slate)]',
    PRIORITIZE: 'bg-[var(--green)]',
  };
  const status = cycle.status ?? 'draft';
  const phase: string = cycle.currentPhase ?? '';
  const cadence: string = cycle.cadence ?? '';
  const snippet: string = cycle.description
    ? cycle.description.slice(0, 140) + (cycle.description.length > 140 ? '…' : '')
    : '';

  return (
    <Link
      to={`/prioritization/cycles/${cycle.id}`}
      className="flex items-start gap-4 p-4 bg-[var(--bg-raised)] border border-[var(--rule)] hover:border-[var(--slate)] hover:shadow-sm transition-all duration-150 group"
    >
      <div className={`w-2 h-2 rounded-full flex-shrink-0 mt-2 ${phaseDots[phase] ?? 'bg-[var(--rule-bright)]'}`} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-[var(--text-pri)] leading-snug mb-0.5">
          <Highlight text={cycle.name ?? 'Untitled Cycle'} query={searchQuery} />
        </div>
        {snippet && (
          <div className="text-xs text-[var(--text-ter)] mb-2 line-clamp-2">
            <Highlight text={snippet} query={searchQuery} />
          </div>
        )}
        <div className="flex flex-wrap items-center gap-1.5">
          <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full border ${statusColors[status] ?? statusColors.draft}`}>
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </span>
          {phase && (
            <span className="px-2 py-0.5 text-[10px] font-medium rounded-full border bg-[var(--hover-tint)] text-[var(--text-ter)] border-[var(--rule)]">
              {phase.charAt(0) + phase.slice(1).toLowerCase()}
            </span>
          )}
          {cadence && (
            <span className="px-2 py-0.5 text-[10px] font-medium rounded-full border bg-[var(--hover-tint)] text-[var(--text-ter)] border-[var(--rule)]">
              {cadence}
            </span>
          )}
          {cycle.itemIds?.length > 0 && (
            <span className="px-2 py-0.5 text-[10px] font-medium rounded-full border bg-[var(--hover-tint)] text-[var(--text-ter)] border-[var(--rule)]">
              {cycle.itemIds.length} item{cycle.itemIds.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>
      <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--slate)]">
          Cycle
        </span>
        <ExternalLink className="h-3.5 w-3.5 text-[var(--text-ter)] group-hover:text-[var(--text-ter)] transition-colors" />
      </div>
    </Link>
  );
}

function TrackCard({ track, searchQuery, cycleName }: { track: any; searchQuery: string; cycleName?: string }) {
  const statusColors: Record<string, string> = {
    active:         'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] border-[var(--green)]',
    accepted:       'bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--slate)]',
    final_rejected: 'bg-[var(--coral-dim)] text-[var(--coral)] border-[var(--coral)]',
    archived:       'bg-[var(--hover-tint)] text-[var(--text-ter)] border-[var(--rule)]',
  };
  const categoryDot = track.requiredItemCategory ? (CATEGORY_DOT[track.requiredItemCategory] ?? 'bg-[var(--rule-bright)]') : 'bg-[var(--rule-bright)]';
  const status: string = track.status ?? 'active';
  const snippet: string = track.description
    ? track.description.slice(0, 140) + (track.description.length > 140 ? '…' : '')
    : '';

  return (
    <Link
      to={`/prioritization/tracks/${track.id}`}
      className="flex items-start gap-4 p-4 bg-[var(--bg-raised)] border border-[var(--rule)] hover:border-[var(--mustard)] hover:shadow-sm transition-all duration-150 group"
    >
      <div className={`w-2 h-2 rounded-full flex-shrink-0 mt-2 ${categoryDot}`} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-[var(--text-pri)] leading-snug mb-0.5">
          <Highlight text={track.name ?? 'Untitled Track'} query={searchQuery} />
        </div>
        {snippet && (
          <div className="text-xs text-[var(--text-ter)] mb-2 line-clamp-2">
            <Highlight text={snippet} query={searchQuery} />
          </div>
        )}
        <div className="flex flex-wrap items-center gap-1.5">
          <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full border ${statusColors[status] ?? statusColors.active}`}>
            {status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
          </span>
          {track.requiredItemCategory && (
            <span className="px-2 py-0.5 text-[10px] font-medium rounded-full border bg-[var(--hover-tint)] text-[var(--text-ter)] border-[var(--rule)]">
              {track.requiredItemCategory}
            </span>
          )}
          {track.requiredAltitudeLevel && (
            <span className="px-2 py-0.5 text-[10px] font-medium rounded-full border bg-[var(--hover-tint)] text-[var(--text-ter)] border-[var(--rule)]">
              L{track.requiredAltitudeLevel}
            </span>
          )}
          {track.isStandalone && (
            <span className="px-2 py-0.5 text-[10px] font-medium rounded-full border bg-[var(--hover-tint)] text-[var(--text-ter)] border-[var(--rule)]">
              Standalone
            </span>
          )}
          {cycleName && (
            <span className="px-2 py-0.5 text-[10px] font-medium rounded-full border bg-[var(--hover-tint)] text-[var(--text-ter)] border-[var(--rule)] truncate max-w-[140px]">
              {cycleName}
            </span>
          )}
        </div>
      </div>
      <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border bg-[var(--mustard-dim)] text-[var(--mustard)] border-[var(--mustard)]">
          Track
        </span>
        <ExternalLink className="h-3.5 w-3.5 text-[var(--text-ter)] group-hover:text-[var(--mustard)] transition-colors" />
      </div>
    </Link>
  );
}

function SessionCard({ session, searchQuery, cycleName, trackName }: { session: any; searchQuery: string; cycleName?: string; trackName?: string }) {
  const stateColors: Record<string, string> = {
    DRAFT:              'bg-[var(--hover-tint)] text-[var(--text-ter)] border-[var(--rule)]',
    CRITERIA_FINALIZED: 'bg-[var(--mustard-dim)] text-[var(--mustard)] border-[var(--mustard)]',
    RUN_COMPLETE:       'bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--slate)]',
    PUBLISHED:          'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] border-[var(--green)]',
  };
  const typeColors: Record<string, string> = {
    engage:  'bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--slate)]',
    gather:  'bg-[var(--mustard-dim)] text-[var(--mustard)] border-[var(--mustard)]',
    arrange: 'bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--slate)]',
  };
  const state: string = session.state ?? 'DRAFT';
  const type: string = session.sessionType ?? '';
  const stateLabel: Record<string, string> = SESSION_STATE_LABELS;
  const snippet: string = session.description
    ? session.description.slice(0, 140) + (session.description.length > 140 ? '…' : '')
    : '';

  const scheduledFor: Date | null = session.scheduledFor ? new Date(session.scheduledFor) : null;
  const schedLabel = scheduledFor
    ? scheduledFor.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    : null;

  return (
    <Link
      to={`/prioritization/sessions/${session.id}`}
      className="flex items-start gap-4 p-4 bg-[var(--bg-raised)] border border-[var(--rule)] hover:border-[var(--green)] hover:shadow-sm transition-all duration-150 group"
    >
      <div className={`w-2 h-2 rounded-full flex-shrink-0 mt-2 ${type === 'engage' ? 'bg-[var(--slate)]' : type === 'gather' ? 'bg-[var(--mustard)]' : type === 'arrange' ? 'bg-[var(--slate)]' : 'bg-[var(--rule-bright)]'}`} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-[var(--text-pri)] leading-snug mb-0.5">
          <Highlight text={session.name ?? 'Untitled Session'} query={searchQuery} />
        </div>
        {snippet && (
          <div className="text-xs text-[var(--text-ter)] mb-2 line-clamp-2">
            <Highlight text={snippet} query={searchQuery} />
          </div>
        )}
        <div className="flex flex-wrap items-center gap-1.5">
          <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full border ${stateColors[state] ?? stateColors.DRAFT}`}>
            {stateLabel[state] ?? state}
          </span>
          {type && (
            <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full border ${typeColors[type] ?? 'bg-[var(--hover-tint)] text-[var(--text-ter)] border-[var(--rule)]'}`}>
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </span>
          )}
          {schedLabel && (
            <span className="px-2 py-0.5 text-[10px] font-medium rounded-full border bg-[var(--hover-tint)] text-[var(--text-ter)] border-[var(--rule)] flex items-center gap-1">
              <Clock className="h-2.5 w-2.5" />
              {schedLabel}
            </span>
          )}
          {cycleName && (
            <span className="px-2 py-0.5 text-[10px] font-medium rounded-full border bg-[var(--hover-tint)] text-[var(--text-ter)] border-[var(--rule)] truncate max-w-[120px]">
              {cycleName}
            </span>
          )}
          {trackName && (
            <span className="px-2 py-0.5 text-[10px] font-medium rounded-full border bg-[var(--hover-tint)] text-[var(--text-ter)] border-[var(--rule)] truncate max-w-[120px]">
              {trackName}
            </span>
          )}
        </div>
      </div>
      <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] border-[var(--green)]">
          Session
        </span>
        <ExternalLink className="h-3.5 w-3.5 text-[var(--text-ter)] group-hover:text-[var(--green)] transition-colors" />
      </div>
    </Link>
  );
}


// ============================================================================

function matchesQuery(text: string, query: string): boolean {
  if (!query.trim()) return true;
  return text.toLowerCase().includes(query.toLowerCase().trim());
}

function isDateInRange(date: Date | string | null | undefined, range: [string, string]): boolean {
  if (!date) return false;
  const d = new Date(date).getTime();
  if (range[0] && d < new Date(range[0]).getTime()) return false;
  if (range[1] && d > new Date(range[1] + 'T23:59:59').getTime()) return false;
  return true;
}

function sortEntities<T extends Record<string, unknown>>(
  items: T[], field: string, dir: 'asc' | 'desc',
): T[] {
  return [...items].sort((a, b) => {
    const av = a[field] as string | number | Date | null | undefined;
    const bv = b[field] as string | number | Date | null | undefined;
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    const aStr = av instanceof Date ? av.toISOString() : String(av);
    const bStr = bv instanceof Date ? bv.toISOString() : String(bv);
    return dir === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
  });
}

function filterCycles(
  cycles: any[],
  query: string,
  facetValues: Record<string, Set<string>>,
  dateRanges: Record<string, [string, string]>,
  sortField: string,
  sortDir: 'asc' | 'desc',
): any[] {
  const result = cycles.filter((c) => {
    if (!matchesQuery(`${c.name ?? ''} ${c.description ?? ''}`, query)) return false;
    if (facetValues.status?.size && !facetValues.status.has(c.status ?? '')) return false;
    if (facetValues.currentPhase?.size && !facetValues.currentPhase.has(c.currentPhase ?? '')) return false;
    if (facetValues.cadence?.size && !facetValues.cadence.has(c.cadence ?? '')) return false;
    if (dateRanges.createdAt && (dateRanges.createdAt[0] || dateRanges.createdAt[1])) {
      if (!isDateInRange(c.createdAt, dateRanges.createdAt)) return false;
    }
    if (dateRanges.startedAt && (dateRanges.startedAt[0] || dateRanges.startedAt[1])) {
      if (!isDateInRange(c.startedAt, dateRanges.startedAt)) return false;
    }
    if (dateRanges.completedAt && (dateRanges.completedAt[0] || dateRanges.completedAt[1])) {
      if (!isDateInRange(c.completedAt, dateRanges.completedAt)) return false;
    }
    return true;
  });
  return sortEntities(result, sortField, sortDir);
}

function filterTracks(
  tracks: any[],
  query: string,
  facetValues: Record<string, Set<string>>,
  dateRanges: Record<string, [string, string]>,
  sortField: string,
  sortDir: 'asc' | 'desc',
): any[] {
  const result = tracks.filter((t) => {
    if (!matchesQuery(`${t.name ?? ''} ${t.description ?? ''}`, query)) return false;
    if (facetValues.status?.size && !facetValues.status.has(t.status ?? '')) return false;
    if (facetValues.requiredItemCategory?.size && !facetValues.requiredItemCategory.has(t.requiredItemCategory ?? '')) return false;
    if (facetValues.requiredAltitudeLevel?.size && !facetValues.requiredAltitudeLevel.has(String(t.requiredAltitudeLevel ?? ''))) return false;
    if (facetValues.isStandalone?.size) {
      const wantsStandalone = facetValues.isStandalone.has('true');
      if (wantsStandalone !== Boolean(t.isStandalone)) return false;
    }
    if (dateRanges.createdAt && (dateRanges.createdAt[0] || dateRanges.createdAt[1])) {
      if (!isDateInRange(t.createdAt, dateRanges.createdAt)) return false;
    }
    return true;
  });
  return sortEntities(result, sortField, sortDir);
}

function filterSessions(
  sessions: any[],
  query: string,
  facetValues: Record<string, Set<string>>,
  dateRanges: Record<string, [string, string]>,
  sortField: string,
  sortDir: 'asc' | 'desc',
): any[] {
  const result = sessions.filter((s) => {
    if (!matchesQuery(`${s.name ?? ''} ${s.description ?? ''}`, query)) return false;
    if (facetValues.sessionType?.size && !facetValues.sessionType.has(s.sessionType ?? '')) return false;
    if (facetValues.state?.size && !facetValues.state.has(s.state ?? '')) return false;
    if (facetValues.cycleId?.size && !facetValues.cycleId.has(s.cycleId ?? '')) return false;
    if (facetValues.trackId?.size && !facetValues.trackId.has(s.trackId ?? '')) return false;
    if (dateRanges.createdAt && (dateRanges.createdAt[0] || dateRanges.createdAt[1])) {
      if (!isDateInRange(s.createdAt, dateRanges.createdAt)) return false;
    }
    if (dateRanges.scheduledFor && (dateRanges.scheduledFor[0] || dateRanges.scheduledFor[1])) {
      if (!isDateInRange(s.scheduledFor, dateRanges.scheduledFor)) return false;
    }
    return true;
  });
  return sortEntities(result, sortField, sortDir);
}

interface SaveDialogProps {
  onSave: (name: string, description: string) => void;
  onCancel: () => void;
  resultCount: number;
}

function SaveDialog({ onSave, onCancel, resultCount }: SaveDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => { nameRef.current?.focus(); }, []);

  return (
    <div className="px-4 py-3 bg-[var(--slate-dim)] border-b border-[var(--slate)] flex-shrink-0">
      <div className="flex items-start gap-3">
        <BookmarkCheck className="h-4 w-4 text-[var(--text-ter)] mt-2.5 flex-shrink-0" />
        <div className="flex-1 space-y-2">
          <div>
            <p className="text-xs font-semibold text-[var(--text-sec)] mb-1">Name this search</p>
            <input
              ref={nameRef}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && name.trim()) onSave(name.trim(), description.trim());
                if (e.key === 'Escape') onCancel();
              }}
              placeholder="e.g. Active risks in Q3 cycle"
              className="w-full text-sm px-3 py-1.5 border border-[var(--slate)] bg-[var(--bg-raised)] focus:outline-none focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 placeholder:text-[var(--text-ter)] text-[var(--text-pri)]"
            />
          </div>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && name.trim()) onSave(name.trim(), description.trim());
              if (e.key === 'Escape') onCancel();
            }}
            placeholder="Optional description"
            className="w-full text-xs px-3 py-1.5 border border-[var(--slate)] bg-[var(--bg-raised)] focus:outline-none focus:border-[var(--slate)] focus:ring-1 focus:ring-indigo-100 placeholder:text-[var(--text-ter)] text-[var(--text-sec)]"
          />
          <div className="flex items-center gap-2">
            <button
              onClick={() => name.trim() && onSave(name.trim(), description.trim())}
              disabled={!name.trim()}
              className="px-3 py-1.5 text-xs font-semibold bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm shadow-indigo-200"
            >
              Save search
            </button>
            <button
              onClick={onCancel}
              className="px-3 py-1.5 text-xs font-medium text-[var(--text-sec)] hover:text-[var(--text-sec)] transition-colors"
            >
              Cancel
            </button>
            {resultCount > 0 && (
              <span className="text-[11px] text-[var(--text-sec)] ml-auto">
                {resultCount.toLocaleString()} result{resultCount !== 1 ? 's' : ''} will be saved
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Saved searches sidebar panel
// ============================================================================

interface SavedSearchesPanelProps {
  searches: SavedSearch[];
  activeId: string | null;
  onLoad: (s: SavedSearch) => void;
  onDelete: (id: string) => void;
}

function SavedSearchesPanel({ searches, activeId, onLoad, onDelete }: SavedSearchesPanelProps) {
  const [open, setOpen] = useState(true);

  if (searches.length === 0) return null;

  return (
    <div className="border-b border-[var(--rule)]">
      <button
        onClick={() => setOpen((p) => !p)}
        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-[var(--surface)]/80 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          <Bookmark className="h-3 w-3 text-[var(--text-ter)]" />
          <span className="text-[11px] font-bold text-[var(--text-ter)] uppercase tracking-widest">
            Saved
          </span>
          <span className="text-[10px] font-bold text-[var(--text-sec)] bg-[var(--slate-dim)] px-1.5 py-0.5 rounded-full">
            {searches.length}
          </span>
        </div>
        {open
          ? <ChevronDown className="h-3.5 w-3.5 text-[var(--text-ter)]" />
          : <ChevronRight className="h-3.5 w-3.5 text-[var(--text-ter)]" />
        }
      </button>

      {open && (
        <div className="pb-2 space-y-0.5 px-2">
          {searches.map((s) => {
            const isActive = s.id === activeId;
            return (
              <div
                key={s.id}
                className={`group flex items-center gap-1.5  px-2 py-1.5 transition-all cursor-pointer ${
                  isActive
                    ? 'bg-[var(--slate-dim)] border border-[var(--slate)]'
                    : 'hover:bg-[var(--surface)] border border-transparent'
                }`}
                onClick={() => onLoad(s)}
              >
                <BookmarkCheck className={`h-3 w-3 flex-shrink-0 ${isActive ? 'text-[var(--text-ter)]' : 'text-[var(--text-ter)] group-hover:text-[var(--text-ter)]'}`} />
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-medium truncate leading-tight ${isActive ? 'text-[var(--text-ter)]' : 'text-[var(--text-sec)]'}`}>
                    {s.name}
                  </p>
                  {s.resultCount !== undefined && (
                    <p className="text-[10px] text-[var(--text-ter)] leading-tight">{s.resultCount.toLocaleString()} items</p>
                  )}
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); onDelete(s.id); }}
                  className="opacity-0 group-hover:opacity-100 p-0.5 text-[var(--text-ter)] hover:text-[var(--coral)] transition-all flex-shrink-0"
                  title="Delete saved search"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Result card
// ============================================================================

function ResultCard({ item, searchQuery }: { item: any; searchQuery: string }) {
  const category = item.itemCategory as string | undefined;
  const badge = category ? (CATEGORY_BADGE[category] ?? CATEGORY_BADGE_FALLBACK) : CATEGORY_BADGE_FALLBACK;
  const dot = category ? (CATEGORY_DOT[category] ?? 'bg-[var(--surface)]') : 'bg-[var(--surface)]';
  const status = (item as any).exit_status ?? 'active';
  const statusColors: Record<string, string> = {
    active:   'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] border-[var(--green)]',
    deferred: 'bg-[var(--mustard-dim)] text-[var(--mustard)] border-[var(--mustard)]',
    archived: 'bg-[var(--hover-tint)] text-[var(--text-ter)] border-[var(--rule)]',
  };
  const altitudeLabel = item.altitudeLevel ? `L${item.altitudeLevel}` : null;
  const snippet = item.description
    ? item.description.slice(0, 160) + (item.description.length > 160 ? '…' : '')
    : null;

  return (
    <Link
      to={`/prioritization/items/${item.id}`}
      className="flex items-start gap-4 p-4 bg-[var(--bg-raised)] border border-[var(--rule)] hover:border-[var(--slate)] hover:shadow-sm transition-all duration-150 group"
    >
      {/* Category dot */}
      <div className={`w-2 h-2 rounded-full flex-shrink-0 mt-2 ${dot}`} />

      <div className="flex-1 min-w-0">
        {/* Title */}
        <div className="text-sm font-semibold text-[var(--text-pri)] leading-snug mb-0.5">
          <Highlight text={item.title ?? 'Untitled'} query={searchQuery} />
        </div>

        {/* Description snippet */}
        {snippet && (
          <div className="text-xs text-[var(--text-ter)] leading-relaxed mb-2 line-clamp-2">
            <Highlight text={snippet} query={searchQuery} />
          </div>
        )}

        {/* Badges */}
        <div className="flex flex-wrap items-center gap-1.5">
          {category && (
            <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full border ${badge.bg} ${badge.text} ${badge.border}`}>
              {category.charAt(0).toUpperCase() + category.slice(1)}
            </span>
          )}
          <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full border ${statusColors[status] ?? statusColors.active}`}>
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </span>
          {altitudeLabel && (
            <span className="px-2 py-0.5 text-[10px] font-medium rounded-full border bg-[var(--hover-tint)] text-[var(--text-ter)] border-[var(--rule)]">
              {altitudeLabel}
            </span>
          )}
          {item.hasCatchballProposal && (
            <span className="px-2 py-0.5 text-[10px] font-medium rounded-full border bg-[var(--hover-tint)] text-[var(--text-ter)] border-[var(--rule)]">
              Catchball
            </span>
          )}
          {item.isLeadershipApproved && (
            <span className="px-2 py-0.5 text-[10px] font-medium rounded-full border bg-[var(--hover-tint)] text-[var(--text-ter)] border-[var(--rule)]">
              Priority List
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--slate)]">
          Item
        </span>
        <ExternalLink className="h-3.5 w-3.5 text-[var(--text-ter)] group-hover:text-[var(--text-ter)] transition-colors" />
      </div>
    </Link>
  );
}

// ============================================================================
// Main component
// ============================================================================

export default function AdvancedSearch() {
  const [searchParams, setSearchParams] = useSearchParams();
  // No ⌘K hint on a phone or a touch screen: there is no key to press.
  const showShortcutHints = useKeyboardShortcutHints();
  const inputRef = useRef<HTMLInputElement>(null);

  // Feature flags — hide flag-gated filter fields when disabled
  const isSubjectTypesEnabled = useFeatureFlag(FEATURE_FLAGS.SUBJECT_TYPES);

  // Saved searches
  const { searches: savedSearches, save: saveSearch, remove: removeSearch } = useSavedSearches();
  const [activeSavedId, setActiveSavedId] = useState<string | null>(null);
  const [showSaveDialog, setShowSaveDialog] = useState(false);

  // ── Entity type — null = aggregated (all types mixed, no sidebar) ───────────
  const entityParam = searchParams.get('entity') as EntityType | null;
  const entityType: EntityType | null = (['items', 'cycles', 'tracks', 'sessions'] as EntityType[]).includes(entityParam as EntityType)
    ? entityParam
    : null;

  const setEntityType = useCallback((et: EntityType) => {
    setFacetValues({});
    setDateRanges({});
    setActiveSavedId(null);
    if (et === entityType) {
      // Clicking the active pill deselects it → aggregated mode
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('entity');
          next.delete('saved');
          return next;
        },
        { replace: true },
      );
      setSortField('updatedAt');
      setSortDir('desc');
    } else {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('entity', et);
          next.delete('saved');
          return next;
        },
        { replace: true },
      );
      const opts = ENTITY_SORT_OPTIONS[et];
      setSortField(opts[0]?.field ?? 'updatedAt');
      setSortDir(opts[0]?.defaultDir ?? 'desc');
    }
  }, [entityType, setSearchParams]);

  // Search query — URL-persisted
  const searchQuery = searchParams.get('q') ?? '';
  const setSearchQuery = useCallback(
    (q: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (q) next.set('q', q); else next.delete('q');
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const [inputValue, setInputValue] = useState(searchQuery);
  useEffect(() => { setInputValue(searchQuery); }, [searchQuery]);

  // Sort state
  const [sortField, setSortField] = useState('updatedAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const sort: SortSpec[] = useMemo(
    () => [{ field: sortField, direction: sortDir }],
    [sortField, sortDir],
  );

  // Facet values: field → Set<string>
  const [facetValues, setFacetValues] = useState<Record<string, Set<string>>>({});
  // Date ranges: field → [start, end]
  const [dateRanges, setDateRanges] = useState<Record<string, [string, string]>>({});

  // Dynamic options from stores
  const orgUnits  = useEnterpriseIdentityStore((s) => s.orgUnits);
  const tracks    = useTrackStore((s) => s.tracks);
  const sessions  = useSessionStore((s) => s.sessions);
  const cycles    = useDegapProcessStore((s) => s.cycles);

  const dynamicOptionsMap = useMemo<Record<string, { value: string; label: string }[]>>(
    () => ({
      queue: Array.from(orgUnits.values())
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((u) => ({ value: u.id, label: u.displayName || u.name })),
      cycleId: (cycles ?? []).map((c) => ({ value: c.id, label: c.name })),
      trackId: (tracks ?? []).map((t) => ({ value: t.id, label: t.name || t.id })),
      sessionId: (sessions ?? []).map((s) => ({
        value: s.id,
        label: s.name || `Session ${s.id.slice(0, 6)}`,
      })),
    }),
    [orgUnits, cycles, tracks, sessions],
  );

  // Convert facets + dateRanges → FilterGroup[] for useItemListData
  // NOTE: visibleFilterSections + visibleFields must be declared ABOVE this block
  // to avoid temporal dead zone errors.

  // Build the active filter sections for the current entity type
  // (items gets feature-flag gating; other entities use their own sections as-is)
  const visibleFilterSections = useMemo(() => {
    if (entityType === null) return [];
    if (entityType !== 'items') return ENTITY_FILTER_SECTIONS[entityType];
    return FILTER_SECTIONS
      .map((section) => ({
        ...section,
        fields: section.fields.filter(
          (f) => f.field !== 'metaCategory' || isSubjectTypesEnabled,
        ),
      }))
      .filter((section) => section.fields.length > 0);
  }, [entityType, isSubjectTypesEnabled]);

  // All visible fields for the current entity (used for filter-group building and chip labels)
  const visibleFields = useMemo(
    () => visibleFilterSections.flatMap((s) => s.fields),
    [visibleFilterSections],
  );

  const filterGroups = useMemo<FilterGroup[]>(() => {
    // In aggregated mode (no entity selected) there are no facets to apply
    if (entityType === null) return [];
    const groups: FilterGroup[] = [];

    for (const [field, vals] of Object.entries(facetValues)) {
      if (vals.size === 0) continue;
      // Skip any field whose flag is currently off
      if (!visibleFields.some((f) => f.field === field)) continue;
      const fieldDef = visibleFields.find((f) => f.field === field);
      const isBool = fieldDef?.type === 'boolean';
      const valArr = Array.from(vals);
      groups.push({
        id: field,
        conjunction: 'OR',
        rules: [
          {
            id: field,
            field,
            label: fieldDef?.label ?? field,
            operator: isBool ? 'boolean' : 'in',
            value: isBool ? (valArr[0] === 'true') : valArr,
            displayValue: valArr
              .map((v) => {
                if (isBool) return v === 'true' ? 'Yes' : 'No';
                const allOpts = [
                  ...(fieldDef?.options ?? []),
                  ...(dynamicOptionsMap[field] ?? []),
                ];
                return allOpts.find((o) => o.value === v)?.label ?? v;
              })
              .join(', '),
          } as FilterRule,
        ],
      });
    }

    for (const [field, [start, end]] of Object.entries(dateRanges)) {
      if (!start && !end) continue;
      const fieldDef = visibleFields.find((f) => f.field === field);
      groups.push({
        id: `${field}-range`,
        conjunction: 'AND',
        rules: [
          {
            id: `${field}-range`,
            field,
            label: fieldDef?.label ?? field,
            operator: 'date-range',
            value: [start, end] as [string, string],
            displayValue: [start && `from ${start}`, end && `to ${end}`].filter(Boolean).join(' '),
          } as FilterRule,
        ],
      });
    }

    return groups;
  }, [entityType, facetValues, dateRanges, dynamicOptionsMap, visibleFields]);

  // Items store
  const items       = useItemsStore((s) => s.items);
  const syncState   = useItemsStore((s) => s.syncState);
  const fetchItems  = useItemsStore((s) => s.fetchFromSupabase);

  const fetchCycles   = useDegapProcessStore((s) => s.fetchFromSupabase);
  const fetchSessions = useSessionStore((s) => s.fetchFromSupabase);
  const fetchAllTracks = useTrackStore((s) => s.fetchAllTracks);

  useEffect(() => { fetchItems(); }, [fetchItems]);
  useEffect(() => { void fetchCycles(); }, [fetchCycles]);
  useEffect(() => { void fetchSessions(); }, [fetchSessions]);
  useEffect(() => { void fetchAllTracks(); }, [fetchAllTracks]);

  // Filtered + sorted rows
  const { rows, totalCount, filteredCount } = useItemListData(
    items,
    searchQuery,
    filterGroups,
    sort,
    null,
  );

  const isLoading = syncState.isLoading && items.length === 0;

  // ── Non-item entity filtered results ─────────────────────────────────────
  // Text-only filtered (no facets) — always computed for pill counts + aggregated view
  const textFilteredCycles = useMemo(
    () => filterCycles(cycles ?? [], searchQuery, {}, {}, 'updatedAt', 'desc'),
    [cycles, searchQuery],
  );

  const textFilteredTracks = useMemo(
    () => filterTracks(tracks ?? [], searchQuery, {}, {}, 'updatedAt', 'desc'),
    [tracks, searchQuery],
  );

  const textFilteredSessions = useMemo(
    () => filterSessions(sessions ?? [], searchQuery, {}, {}, 'updatedAt', 'desc'),
    [sessions, searchQuery],
  );

  // Facet+sort filtered — only meaningful when the entity tab is active
  const filteredCycles = useMemo(() =>
    entityType === 'cycles'
      ? filterCycles(cycles ?? [], searchQuery, facetValues, dateRanges, sortField, sortDir)
      : textFilteredCycles,
    [entityType, cycles, searchQuery, facetValues, dateRanges, sortField, sortDir, textFilteredCycles],
  );

  const filteredTracks = useMemo(() =>
    entityType === 'tracks'
      ? filterTracks(tracks ?? [], searchQuery, facetValues, dateRanges, sortField, sortDir)
      : textFilteredTracks,
    [entityType, tracks, searchQuery, facetValues, dateRanges, sortField, sortDir, textFilteredTracks],
  );

  const filteredSessions = useMemo(() =>
    entityType === 'sessions'
      ? filterSessions(sessions ?? [], searchQuery, facetValues, dateRanges, sortField, sortDir)
      : textFilteredSessions,
    [entityType, sessions, searchQuery, facetValues, dateRanges, sortField, sortDir, textFilteredSessions],
  );

  // Lookup maps for cycle/track names (for session + track cards)
  const cycleNameById = useMemo(() => {
    const m: Record<string, string> = {};
    for (const c of (cycles ?? [])) m[c.id] = c.name ?? c.id;
    return m;
  }, [cycles]);

  const trackNameById = useMemo(() => {
    const m: Record<string, string> = {};
    for (const t of (tracks ?? [])) m[t.id] = t.name ?? t.id;
    return m;
  }, [tracks]);

  // Unified result count for the active entity tab (facet-filtered)
  const entityResultCount =
    entityType === 'items' ? filteredCount :
    entityType === 'cycles' ? filteredCycles.length :
    entityType === 'tracks' ? filteredTracks.length :
    entityType === 'sessions' ? filteredSessions.length :
    0; // aggregated mode — unused in this context

  const entityTotalCount =
    entityType === 'items' ? totalCount :
    entityType === 'cycles' ? (cycles?.length ?? 0) :
    entityType === 'tracks' ? (tracks?.length ?? 0) :
    entityType === 'sessions' ? (sessions?.length ?? 0) :
    0;

  // Aggregated results: all entity types mixed by recency (shown when no entity tab is selected)
  type AggResult =
    | { kind: 'item'; data: (typeof rows)[number] }
    | { kind: 'cycle'; data: (typeof textFilteredCycles)[number] }
    | { kind: 'track'; data: (typeof textFilteredTracks)[number] }
    | { kind: 'session'; data: (typeof textFilteredSessions)[number] };

  const aggregatedResults = useMemo((): AggResult[] => {
    if (!searchQuery || entityType !== null) return [];
    const all: AggResult[] = [
      ...rows.map((d): AggResult => ({ kind: 'item', data: d })),
      ...textFilteredCycles.map((d): AggResult => ({ kind: 'cycle', data: d })),
      ...textFilteredTracks.map((d): AggResult => ({ kind: 'track', data: d })),
      ...textFilteredSessions.map((d): AggResult => ({ kind: 'session', data: d })),
    ];
    return all.sort((a, b) => {
      const getDate = (r: AggResult): number => {
        const raw = r.kind === 'item' ? (r.data as any).updatedAt
          : r.kind === 'cycle' ? (r.data as any).updatedAt
          : r.kind === 'track' ? ((r.data as any).updatedAt ?? (r.data as any).createdAt)
          : ((r.data as any).updatedAt ?? (r.data as any).createdAt);
        return raw ? new Date(raw).getTime() : 0;
      };
      return getDate(b) - getDate(a);
    });
  }, [searchQuery, entityType, rows, textFilteredCycles, textFilteredTracks, textFilteredSessions]);

  // Facet toggle
  const toggleFacet = useCallback((field: string, value: string) => {
    setFacetValues((prev) => {
      const current = new Set(prev[field] ?? []);
      if (current.has(value)) current.delete(value);
      else current.add(value);
      const next = { ...prev, [field]: current };
      if (next[field].size === 0) delete next[field];
      return next;
    });
  }, []);

  // Date setter
  const setDate = useCallback((field: string, end: 'start' | 'end', value: string) => {
    setDateRanges((prev) => {
      const current = prev[field] ?? ['', ''];
      const next: [string, string] = end === 'start'
        ? [value, current[1]]
        : [current[0], value];
      if (!next[0] && !next[1]) {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      }
      return { ...prev, [field]: next };
    });
  }, []);

  // Clear everything
  const clearAll = useCallback(() => {
    setFacetValues({});
    setDateRanges({});
    setSearchQuery('');
    setInputValue('');
    setActiveSavedId(null);
    setSortField('updatedAt');
    setSortDir('desc');
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('q');
      next.delete('saved');
      next.delete('entity');
      return next;
    }, { replace: true });
  }, [setSearchQuery, setSearchParams]);

  // Remove single field
  const removeFacet = useCallback((field: string) => {
    setFacetValues((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
    setDateRanges((prev) => {
      const next = { ...prev };
      delete next[`${field}-range`];
      delete next[field];
      return next;
    });
  }, []);

  // Active filter chips
  const activeChips: ActiveChip[] = useMemo(() => {
    const chips: ActiveChip[] = [];
    for (const group of filterGroups) {
      for (const rule of group.rules) {
        const fieldDef = visibleFields.find((f) => f.field === rule.field);
        chips.push({
          field: rule.field,
          label: fieldDef?.label ?? rule.field,
          displayValue: rule.displayValue,
          onRemove: () => removeFacet(rule.field),
        });
      }
    }
    return chips;
  }, [filterGroups, removeFacet]);

  // Apply a saved search (restore all state from a SavedSearch object)
  const applySearch = useCallback((s: SavedSearch) => {
    setInputValue(s.searchQuery);
    setSearchQuery(s.searchQuery);
    setFacetValues(
      Object.fromEntries(
        Object.entries(s.facetValues).map(([k, v]) => [k, new Set(v)]),
      ),
    );
    setDateRanges(s.dateRanges as Record<string, [string, string]>);
    setSortField(s.sortField);
    setSortDir(s.sortDir);
    setActiveSavedId(s.id);
    // Update URL to reflect saved id
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (s.searchQuery) next.set('q', s.searchQuery); else next.delete('q');
        next.set('saved', s.id);
        return next;
      },
      { replace: true },
    );
  }, [setSearchQuery, setSearchParams]);

  // Save the current search state
  const handleSave = useCallback((name: string, description: string) => {
    const newSearch = saveSearch({
      name,
      description: description || undefined,
      searchQuery,
      facetValues: Object.fromEntries(
        Object.entries(facetValues).map(([k, v]) => [k, Array.from(v)]),
      ),
      dateRanges,
      sortField,
      sortDir,
      resultCount: entityResultCount,
    });
    setActiveSavedId(newSearch.id);
    setShowSaveDialog(false);
    // Reflect in URL
    setSearchParams(
      (prev) => { const next = new URLSearchParams(prev); next.set('saved', newSearch.id); return next; },
      { replace: true },
    );
  }, [saveSearch, searchQuery, facetValues, dateRanges, sortField, sortDir, entityResultCount, setSearchParams]);

  // Sort direction toggle
  const handleSortFieldChange = (field: string) => {
    const et = entityType ?? 'items';
    const opt = ENTITY_SORT_OPTIONS[et].find((o) => o.field === field);
    setSortField(field);
    setSortDir(opt?.defaultDir ?? 'desc');
  };

  const totalFilters = filterGroups.length;
  const hasFilters = totalFilters > 0 || searchQuery.length > 0;

  // Show the full interface once the user has typed or chosen an entity tab.
  // Before that: only the header + centred search bar are visible.
  const showInterface = searchQuery.length > 0 || searchParams.has('entity') || searchParams.has('q');

  // Cmd+K focus
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Load a saved search from URL param (?saved=<id>) on mount
  const loadedSavedIdRef = useRef<string | null>(null);
  useEffect(() => {
    const savedId = searchParams.get('saved');
    if (!savedId || loadedSavedIdRef.current === savedId) return;
    const found = getSavedSearches().find((s) => s.id === savedId);
    if (!found) return;
    loadedSavedIdRef.current = savedId;
    applySearch(found);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="h-[calc(100vh-6rem)] flex flex-col bg-[var(--bg-raised)] border border-[var(--rule)]/80 shadow-sm -mx-1 overflow-hidden">

      {/* ── Page header ───────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-[var(--rule)] flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-8 h-8 bg-[var(--slate-dim)]">
            <Search className="h-4 w-4 text-[var(--text-ter)]" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-[var(--text-pri)] leading-tight">Search</h1>
            {showInterface && (
              <p className="text-[11px] text-[var(--text-ter)] leading-tight">
                {isLoading ? 'Loading…' : entityType !== null
                  ? (hasFilters
                      ? `${entityResultCount.toLocaleString()} of ${entityTotalCount.toLocaleString()} ${ENTITY_META[entityType].pluralLabel}`
                      : `${entityTotalCount.toLocaleString()} ${ENTITY_META[entityType].pluralLabel} total`)
                  : `${aggregatedResults.length.toLocaleString()} results across all types`}
              </p>
            )}
          </div>
        </div>

        {/* Sort + save + reset — only when an entity tab is active */}
        {showInterface && entityType !== null && (
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <ArrowUpDown className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
              <select
                value={sortField}
                onChange={(e) => handleSortFieldChange(e.target.value)}
                className="text-sm border border-[var(--rule)] bg-[var(--surface)] px-2.5 py-1.5 text-[var(--text-sec)] focus:outline-none focus:border-[var(--slate)] focus:ring-1 focus:ring-indigo-100 transition-colors"
              >
                {ENTITY_SORT_OPTIONS[entityType].map((o) => (
                  <option key={o.field} value={o.field}>{o.label}</option>
                ))}
              </select>
              <button
                onClick={() => setSortDir((d) => d === 'asc' ? 'desc' : 'asc')}
                title={sortDir === 'desc' ? 'Sort descending' : 'Sort ascending'}
                className="p-1.5 border border-[var(--rule)] text-[var(--text-ter)] hover:border-[var(--slate)] hover:text-[var(--text-ter)] bg-[var(--surface)] hover:bg-[var(--slate-dim)] transition-all"
              >
                <span className="text-xs font-mono">{sortDir === 'desc' ? '↓' : '↑'}</span>
              </button>
            </div>
            {hasFilters && (
              <button
                onClick={() => setShowSaveDialog((p) => !p)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border  transition-all ${
                  showSaveDialog || activeSavedId
                    ? 'bg-[var(--slate-dim)] border-[var(--slate)] text-[var(--text-sec)]'
                    : 'text-[var(--text-sec)] border-[var(--rule)] hover:bg-[var(--slate-dim)] hover:border-[var(--slate)] hover:text-[var(--text-ter)]'
                }`}
                title={activeSavedId ? 'Save as new' : 'Save this search'}
              >
                <Bookmark className="h-3.5 w-3.5" />
                {activeSavedId ? 'Saved' : 'Save search'}
              </button>
            )}
            {hasFilters && (
              <button
                onClick={clearAll}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[var(--text-ter)] hover:text-[var(--text-sec)] border border-[var(--rule)] hover:bg-[var(--surface)] transition-all"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Reset
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── Search bar — always compact at the top ────────────────── */}
      <div className="px-4 py-3 border-b border-[var(--rule)] bg-[var(--surface)]/50 flex-shrink-0">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-ter)] pointer-events-none" />
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              setSearchQuery(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') { setInputValue(''); setSearchQuery(''); }
            }}
            placeholder={`Search items, cycles, tracks, and sessions…${showShortcutHints ? ' ⌘K' : ''}`}
            className="w-full pl-11 pr-10 py-2.5 text-sm bg-[var(--bg-raised)] border border-[var(--rule)] text-[var(--text-pri)] placeholder:text-[var(--text-ter)] focus:bg-[var(--bg-raised)] focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 transition-colors outline-none shadow-sm"
            autoFocus
          />
          {inputValue && (
            <button
              onClick={() => { setInputValue(''); setSearchQuery(''); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 text-[var(--text-ter)] hover:text-[var(--text-sec)] rounded"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* ── Everything below only shown once the user has typed a query ── */}
      {!showInterface ? (
        <div className="flex-1 flex items-center justify-center">
          <p className="text-sm text-[var(--text-ter)]">Search across items, cycles, tracks, and sessions</p>
        </div>
      ) : (
        <>
          {/* ── Entity type selector ────────────────────────────────── */}
          <EntityTabs
            value={entityType}
            counts={{
              items:    entityType === 'items' ? entityResultCount : filteredCount,
              cycles:   entityType === 'cycles' ? entityResultCount : textFilteredCycles.length,
              tracks:   entityType === 'tracks' ? entityResultCount : textFilteredTracks.length,
              sessions: entityType === 'sessions' ? entityResultCount : textFilteredSessions.length,
            }}
            onChange={setEntityType}
          />

          {/* ── Save dialog ──────────────────────────────────────────── */}
          {showSaveDialog && entityType !== null && (
            <SaveDialog
              resultCount={entityResultCount}
              onSave={handleSave}
              onCancel={() => setShowSaveDialog(false)}
            />
          )}

          {/* ── Active filters bar (only when entity tab is active) ── */}
          {entityType !== null && (
            <ActiveFilterChips chips={activeChips} onClearAll={clearAll} />
          )}

          {/* ── Main layout ─────────────────────────────────────────── */}
          <div className="flex-1 flex overflow-hidden">

            {/* Left sidebar: only visible when an entity tab is selected */}
            {entityType !== null && (
              <aside className="w-64 flex-shrink-0 border-r border-[var(--rule)]/80 bg-[var(--bg-raised)] overflow-y-auto">
                <div className="px-4 py-2.5 border-b border-[var(--rule)] bg-[var(--surface)]/50 sticky top-0 z-10">
                  <div className="flex items-center gap-2">
                    <SlidersHorizontal className="h-3.5 w-3.5 text-[var(--text-ter)]" />
                    <span className="text-xs font-semibold text-[var(--text-sec)]">Filters</span>
                    {totalFilters > 0 && (
                      <span className="ml-auto text-[10px] font-bold text-[var(--text-sec)] bg-[var(--slate-dim)] px-1.5 py-0.5 rounded-full">
                        {totalFilters}
                      </span>
                    )}
                  </div>
                </div>

                <SavedSearchesPanel
                  searches={savedSearches}
                  activeId={activeSavedId}
                  onLoad={applySearch}
                  onDelete={(id) => {
                    removeSearch(id);
                    if (activeSavedId === id) setActiveSavedId(null);
                  }}
                />

                {visibleFilterSections.map((section) => (
                  <FacetSection
                    key={section.label}
                    section={section}
                    facetValues={facetValues}
                    dateRanges={dateRanges}
                    dynamicOptionsMap={dynamicOptionsMap}
                    onToggleFacet={toggleFacet}
                    onSetDate={setDate}
                  />
                ))}
              </aside>
            )}

            {/* Right: results */}
            <main className="flex-1 overflow-y-auto bg-[var(--surface)]/40">
              {/* ── Aggregated results (no entity tab selected) ── */}
              {entityType === null && (
                aggregatedResults.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full gap-3 text-center p-8">
                    <AlertCircle className="h-10 w-10 text-[var(--text-ter)]" />
                    <p className="text-sm font-medium text-[var(--text-sec)]">No results match "{searchQuery}"</p>
                    <button
                      onClick={clearAll}
                      className="mt-2 px-4 py-2 text-sm font-medium text-[var(--text-sec)] bg-[var(--slate-dim)] hover:bg-[var(--slate-dim)] transition-colors"
                    >
                      Clear search
                    </button>
                  </div>
                ) : (
                  <div className="p-4 space-y-2">
                    <p className="text-xs text-[var(--text-ter)] mb-3">
                      <span className="font-semibold text-[var(--text-sec)]">{aggregatedResults.length.toLocaleString()}</span>
                      {' '}results — select a pill above to filter by type
                    </p>
                    {aggregatedResults.map((r, i) => {
                      if (r.kind === 'item') return <ResultCard key={`item-${r.data.id}`} item={r.data} searchQuery={searchQuery} />;
                      if (r.kind === 'cycle') return <CycleCard key={`cycle-${r.data.id}`} cycle={r.data} searchQuery={searchQuery} />;
                      if (r.kind === 'track') return (
                        <TrackCard
                          key={`track-${r.data.id}`}
                          track={r.data}
                          searchQuery={searchQuery}
                          cycleName={r.data.cycleId ? cycleNameById[r.data.cycleId] : undefined}
                        />
                      );
                      if (r.kind === 'session') return (
                        <SessionCard
                          key={`session-${r.data.id}-${i}`}
                          session={r.data}
                          searchQuery={searchQuery}
                          cycleName={r.data.cycleId ? cycleNameById[r.data.cycleId] : undefined}
                          trackName={r.data.trackId ? trackNameById[r.data.trackId] : undefined}
                        />
                      );
                      return null;
                    })}
                  </div>
                )
              )}

              {/* ── Entity-specific results (entity tab selected) ── */}
              {entityType !== null && (
                isLoading && entityType === 'items' ? (
                  <div className="flex flex-col items-center justify-center h-full gap-3 text-[var(--text-ter)]">
                    <Loader2 className="h-8 w-8 animate-spin" />
                    <p className="text-sm">Loading items…</p>
                  </div>
                ) : entityResultCount === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full gap-3 text-center p-8">
                    <AlertCircle className="h-10 w-10 text-[var(--text-ter)]" />
                    <div>
                      <p className="text-sm font-medium text-[var(--text-sec)]">
                        {hasFilters
                          ? `No ${ENTITY_META[entityType].pluralLabel} match your search`
                          : `No ${ENTITY_META[entityType].pluralLabel} yet`}
                      </p>
                      {hasFilters && (
                        <p className="text-xs text-[var(--text-ter)] mt-1">
                          Try adjusting your filters or search query
                        </p>
                      )}
                    </div>
                    {hasFilters && (
                      <button
                        onClick={clearAll}
                        className="mt-2 px-4 py-2 text-sm font-medium text-[var(--text-sec)] bg-[var(--slate-dim)] hover:bg-[var(--slate-dim)] transition-colors"
                      >
                        Clear all filters
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="p-4 space-y-2">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-xs text-[var(--text-ter)]">
                        <span className="font-semibold text-[var(--text-sec)]">{entityResultCount.toLocaleString()}</span>
                        {entityTotalCount !== entityResultCount && (
                          <> of <span className="font-semibold text-[var(--text-sec)]">{entityTotalCount.toLocaleString()}</span></>
                        )}
                        {' '}{ENTITY_META[entityType].pluralLabel}
                        {hasFilters && <span className={`ml-1 ${ENTITY_META[entityType].color}`}>— filtered</span>}
                      </p>
                      {hasFilters && (
                        <button onClick={clearAll} className="text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] transition-colors">
                          Clear all
                        </button>
                      )}
                    </div>
                    {entityType === 'items' && rows.map((item) => (
                      <ResultCard key={item.id} item={item} searchQuery={searchQuery} />
                    ))}
                    {entityType === 'cycles' && filteredCycles.map((cycle) => (
                      <CycleCard key={cycle.id} cycle={cycle} searchQuery={searchQuery} />
                    ))}
                    {entityType === 'tracks' && filteredTracks.map((track) => (
                      <TrackCard
                        key={track.id}
                        track={track}
                        searchQuery={searchQuery}
                        cycleName={track.cycleId ? cycleNameById[track.cycleId] : undefined}
                      />
                    ))}
                    {entityType === 'sessions' && filteredSessions.map((session) => (
                      <SessionCard
                        key={session.id}
                        session={session}
                        searchQuery={searchQuery}
                        cycleName={session.cycleId ? cycleNameById[session.cycleId] : undefined}
                        trackName={session.trackId ? trackNameById[session.trackId] : undefined}
                      />
                    ))}
                  </div>
                )
              )}
            </main>
          </div>
        </>
      )}
    </div>
  );
}
